<script lang="ts">
	/**
	 * Collapsible content section built on the `widget` / `bg-depth` primitives.
	 * Use this instead of Flowbite's `Card` for page sections — it participates
	 * in the depth system, so nesting panels reads correctly in both themes.
	 *
	 * Snippets: `children` (body), `titleSnippet` (inline, after the caret),
	 * `titleRight` (pushed to the far right of the title bar).
	 */

	import { slide } from 'svelte/transition';
	import type { Snippet } from 'svelte';

	interface Props {
		/** Heading text. Omit to render an icon-only or snippet-only title bar. */
		title?: string;
		/** Font Awesome solid icon name, without the `fa-` prefix. */
		faIcon?: string;
		/** Extra classes for the icon (colour, size). */
		faClass?: string;
		/** Two-way bindable collapse state. */
		isHidden?: boolean;
		/** Extra classes on the outer wrapper. */
		class?: string;
		/** Extra classes on the title bar. */
		titleClass?: string;
		/** Extra classes on the content area. */
		contentClass?: string;
		/** Tighter padding and a smaller title, for panels inside panels. */
		compact?: boolean;
		/** Drops the widget chrome, leaving only the title bar and body. */
		borderless?: boolean;
		/** Extra content in the title bar (e.g. badges, buttons). */
		titleSnippet?: Snippet;
		/** Right-aligned content in the title bar. */
		titleRight?: Snippet;
		/** Panel body content. */
		children?: Snippet;
	}

	let {
		title = '',
		faIcon = '',
		faClass = '',
		isHidden = $bindable(false),
		class: className = '',
		titleClass = '',
		contentClass = '',
		compact = false,
		borderless = false,
		titleSnippet,
		titleRight,
		children
	}: Props = $props();

	/** Toggle collapse on title-bar click, ignoring interactive children. */
	const fnOnPanelTitleClick = (e: Event) => {
		/* Controls placed in the title snippets must not toggle the panel. */
		if (e.target instanceof HTMLElement) {
			if (e.target.closest('button, a, input, label, select')) {
				return;
			}
		}
		isHidden = !isHidden;
	};
</script>

<div
	class="{className} {compact ? 'px-4 py-2' : ''} panelGroup {borderless
		? ''
		: 'widget'} group/panelwrapper"
>
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<!-- svelte-ignore a11y_click_events_have_key_events -->
	<div
		class="{titleClass} {compact
			? 'text-lg'
			: ''} widget-title group/panel mb-0 flex cursor-pointer items-center gap-4 text-gray-900 dark:text-white"
		onclick={fnOnPanelTitleClick}
	>
		{#if faIcon.length > 0}
			<i
				class="fa-solid fa-{faIcon} {faClass} group-hover/panel:text-primary-500 dark:group-hover/panel:text-primary-500"
			></i>
		{/if}
		{#if title.length > 0}
			<span
				class="shrink-0 group-hover/panel:text-primary-500 dark:group-hover/panel:text-primary-500"
				>{title}</span
			>
		{/if}
		<i
			class="fa-solid text-base {isHidden
				? 'fa-angle-down'
				: 'fa-angle-up'} group-hover/panel:text-primary-500 dark:group-hover/panel:text-primary-500"
		></i>
		{@render titleSnippet?.()}
		<span class="grow"></span>
		{@render titleRight?.()}
	</div>
	{#if !isHidden}
		<div transition:slide class="{contentClass} panelContent {compact ? 'mt-2 pb-2' : 'mt-5'}">
			{@render children?.()}
		</div>
	{/if}
</div>
